import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { migrateLegacySession } from '@/lib/legacySession';
import { UI_STORAGE_KEY } from '@/lib/appScope';
import { DEFAULT_LANG, SUPPORTED_LANGS, ensureLanguage, isLoaded, translate } from '@/i18n';

migrateLegacySession();

const VIEWS = ['dispatcher', 'command', 'approvals', 'alerts', 'client', 'data', 'drivers', 'orders', 'billing', 'analytics', 'fleet', 'telegram', 'leaves', 'admin'];

/**
 * Global UI state: which tab is open, which language, and the live-stream
 * indicator. Deliberately NOT server data — anything fetched from the API
 * belongs to React Query (see src/lib/queryClient.js), which is the whole
 * point of running the two side by side: this store never holds a copy of
 * an order or a vehicle that could go stale.
 *
 * Language and the active view persist to localStorage under the same keys
 * the monolith used, so an existing user's tab and language survive the
 * cutover instead of silently resetting to defaults.
 */
export const useUiStore = create(
  persist(
    (set) => ({
      view: 'dispatcher',
      lang: DEFAULT_LANG,
      /**
       * 'connecting' | 'live' | 'reconnecting' | 'capped' | 'offline' | 'down'
       * — rendered by SSEBadge, set by services/sse.js.
       */
      streamState: 'connecting',
      /**
       * The two numbers the backend gave when it refused this tab a live
       * stream — {scope, active, limit} — or null when it has not. Null is
       * the honest value before any refusal: the badge renders it as an
       * em-dash rather than inventing a count it was never told.
       */
      streamLimit: null,
      lastEvent: null,
      /** { serverTs, receivedAt } from the most recent SSE heartbeat frame —
       *  see services/sse.js. Freshness signal only, never a latency number. */
      lastHeartbeat: null,
      /**
       * V-309. The MESSAGE of the newest bell notification the admin has
       * read — see useNotifications()'s own V-222 comment for why message,
       * not id. Belongs here, not in a useState local to useNotifications()
       * itself: Header/NotificationBell and BottomNav both stay mounted
       * across every view switch (see App.jsx), while AlertsView remounts
       * fresh on every visit (ViewErrorBoundary's key={view}) — three
       * independent hook instances, each with its own local "seen" state,
       * agreeing only at the instant each one first mounted. AlertsView's
       * own markSeen() call used to update only ITS instance's local state
       * plus a raw localStorage write neither of the other two, already
       * mounted, ever read again — so the header bell and the mobile
       * bottom-nav badge stayed stuck at the pre-visit count for the rest
       * of the session, directly contradicting this view's own comment
       * ("opening this view IS reading them"). One shared field fixes all
       * three at once for free, the same way lastEvent/streamState already
       * do for every other cross-component SSE-driven signal.
       */
      lastSeenNotification: null,

      setView: (view) => set({ view: VIEWS.includes(view) ? view : 'dispatcher' }),
      /**
       * V-98: only Lithuanian is compiled into the bundle, so switching to a
       * language whose catalogue is still on the server waits for it. The
       * alternative is a screen that repaints in Lithuanian first and in the
       * chosen language a moment later — and it would do that on EVERY
       * switch, because the fallback inside translate() is Lithuanian.
       *
       * A language already in memory still switches synchronously. That is
       * not an optimisation: a `set` deferred to a microtask makes every
       * caller of this store asynchronous, including the tests that assert
       * what the toggle does.
       */
      setLang: (lang) => {
        if (!SUPPORTED_LANGS.includes(lang)) return;
        if (isLoaded(lang)) {
          set({ lang });
          return;
        }
        ensureLanguage(lang).then((ready) => {
          if (ready) set({ lang });
        });
      },
      setStreamState: (streamState) => set({ streamState }),
      setStreamLimit: (streamLimit) => set({ streamLimit }),
      setLastEvent: (lastEvent) => set({ lastEvent }),
      setLastHeartbeat: (lastHeartbeat) => set({ lastHeartbeat }),
      setLastSeenNotification: (lastSeenNotification) => set({ lastSeenNotification }),
    }),
    {
      name: UI_STORAGE_KEY,
      // V-309. lastSeenNotification joins view/lang here (not left transient
      // like lastEvent/streamState) for the same reason those two are
      // persisted: a badge that resets on every reload trains people to
      // ignore it -- useNotifications()'s own docstring says so. This
      // replaces that hook's previous standalone
      // localStorage['aragela_notif_last_seen_id'] key; an admin's already-
      // read count resets once on first load after this ships (nothing
      // migrates the old key), then persists normally from here on.
      partialize: (s) => ({ view: s.view, lang: s.lang, lastSeenNotification: s.lastSeenNotification }),
    },
  ),
);

/**
 * `const t = useT()` then `t('nav.orders')` / `t('analytics.lastNDays', { n: 30 })`.
 * Subscribes to the language slice only, so switching tabs does not
 * re-render every translated node in the tree.
 */
export function useT() {
  const lang = useUiStore((s) => s.lang);
  return (key, vars) => translate(lang, key, vars);
}
